import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { stopService } from '../../../../application/StopService';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stops/Stop';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseUpdateStopProps {
  route: Route;
}

interface UseUpdateStopMutationProps {
  stop: Stop;
  stopId: string;
}

type UseUpdateStopResponseProps = ServiceResponse<({ stop, stopId }: UseUpdateStopMutationProps) => void, Stop>;

// Hooks act as 'Adapter' layer.
export const useUpdateStop = ({ route }: UseUpdateStopProps): UseUpdateStopResponseProps => {
  const queryClient = useQueryClient();

  const { routeDispatch } = useRouteContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isError, isSuccess, mutate, data } = useMutation<
    Stop,
    unknown,
    UseUpdateStopMutationProps,
    {
      newRoute: Route;
      previousContent?: Route;
    }
  >(({ stop, stopId }: UseUpdateStopMutationProps) => stopService.updateStop({ stop, stopId }), {
    onSuccess: (stop) => {
      const mutationKey = getRouteMutationKey({ routeId: route.id });

      const previousQueryContent = queryClient.getQueryData<Route>(mutationKey);

      if (!previousQueryContent) {
        throw new Error(`previousQueryContent does not exist`);
      }

      previousQueryContent.stops.find((s) => s.id === stop.id)?.updateByStop(stop);

      routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: previousQueryContent });

      queryClient.setQueryData<Route>(mutationKey, previousQueryContent);
    },
    onError: (err: any, _variables: UseUpdateStopMutationProps, context: any) => {
      console.info(`Update Stop Error:`, err);

      if (err?.response?.status === 401) {
        sessionExpiredLogout();
        return;
      }
      showSnackbarMessage({
        messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
      });
    },
  });

  return {
    run: mutate,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: data,
  };
};
